import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import { getSupportProduct } from "./supportProduct";
import { supabaseSelectMaybeSingle } from "../lib/supabaseApi";
import { getCachedFingerprint } from "../lib/browserFingerprint";

/**
 * Tracker générique d'usage réel des fonctions IA vs manuelles de création
 * de sondage. Complète PollModificationTracker (dédié à "poll_modification_ai")
 * avec les événements qui manquaient pour répondre à la question produit :
 * "les gens créent/éditent avec l'IA, ou à la main, et à quel niveau selon
 * leur plan ?"
 *
 * Insert direct dans `analytics_events` (même pattern que
 * PollModificationTracker) : best-effort, jamais bloquant pour le flow UI.
 */

export type UsageEventType =
  | "chat_message_sent"
  | "chat_ai_response"
  | "ai_generation_applied"
  | "manual_edit"
  | "poll_reopened_for_edit"
  // Autorisés par la liste blanche RLS de `analytics_events` depuis la
  // migration 20260921136000. Sans l'entrée dans le type, l'appelant ne
  // compilerait pas ; sans l'entrée dans la liste blanche, l'insert échouerait
  // SILENCIEUSEMENT (le tracker avale l'erreur RLS). Les deux vont ensemble.
  //
  // `trial_banner_clicked` a été RETIRÉ de cette union le 22/09 : #1307 a
  // unifié tous les clics de CTA sous `upgrade_cta_clicked`, distingués par
  // `source`, et plus personne ne l'émettait. Il restait donc ici à suggérer un
  // émetteur inexistant. L'indicateur « essai remarqué » que le plan lui
  // destinait se lit désormais :
  //
  //   WHERE event_type = 'upgrade_cta_clicked'
  //     AND event_data->>'source' = 'banner'
  //
  // La liste blanche RLS le tolère encore, et c'est sans effet : rien ne
  // l'insère. L'en retirer coûterait une migration sur une politique qui a déjà
  // perdu un type en cours de réécriture (NAJ-172) — le risque dépasse le gain.
  | "trial_started"
  | "feature_gate_seen"
  | "feature_used"
  | "upgrade_cta_clicked";

export interface UsageEventData {
  pollId?: string | null;
  isEdit?: boolean;
  [key: string]: unknown;
}

export class UsageAnalyticsTracker {
  /**
   * Résout le plan_type du produit courant pour l'utilisateur connecté.
   * "anonymous" si pas de session, "free" si connecté sans ligne
   * d'abonnement ou en cas d'échec de lecture (fail-closed, même logique
   * que overlayCurrentProductSubscription dans AuthContext).
   */
  private static async resolvePlanType(userId: string | null): Promise<string> {
    if (!userId) return "anonymous";
    try {
      const sub = await supabaseSelectMaybeSingle<{ plan_type: string }>(
        "product_subscriptions",
        {
          user_id: `eq.${userId}`,
          product: `eq.${getSupportProduct()}`,
          select: "plan_type",
        },
        { timeout: 3000 },
      );
      return sub?.plan_type ?? "free";
    } catch {
      return "free";
    }
  }

  static async track(
    eventType: UsageEventType,
    data: UsageEventData = {},
  ): Promise<void> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const planType = await UsageAnalyticsTracker.resolvePlanType(
        user?.id ?? null,
      );

      // Sans empreinte, les événements invités (user_id NULL) ne peuvent pas
      // être rattachés au compte créé ensuite, ni distingués entre invités.
      let guestFingerprint: string | undefined;
      if (!user) {
        try {
          guestFingerprint = await getCachedFingerprint();
        } catch {
          guestFingerprint = undefined;
        }
      }

      const { error } = await supabase.from("analytics_events").insert({
        event_type: eventType,
        event_data: {
          ...data,
          product: getSupportProduct(),
          planType,
          ...(guestFingerprint ? { guestFingerprint } : {}),
        },
        user_id: user?.id ?? null,
      });

      if (error) {
        logger.debug(
          `UsageAnalyticsTracker.track skipped (${eventType})`,
          "analytics",
          { code: error.code, message: error.message },
        );
      }
    } catch (err) {
      logger.debug(
        `UsageAnalyticsTracker.track skipped (${eventType}, exception)`,
        "analytics",
        { error: err instanceof Error ? err.message : String(err) },
      );
    }
  }
}
